import { type Json } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

/**
 * Tabele, do których pisze symulator. Wartości są dosłownymi nazwami tabel — trafiają do
 * `simulator_write_log.tableName` i po nich idzie czyszczenie.
 */
export const SIMULATOR_TABLE = {
  workoutActivity: "client_workout_activity",
  nutritionDay: "client_nutrition_day",
  habitCompletion: "habit_completion",
  challengeTaskCompletion: "challenge_task_completion",
  measurement: "measurement",
  exerciseRecording: "exercise_recording",
} as const;

export type SimulatorTable = (typeof SIMULATOR_TABLE)[keyof typeof SIMULATOR_TABLE];

/**
 * Kasowanie wiersza nie zawsze odpala ten sam łańcuch co jego dodanie: część triggerów
 * webhook-mappera jest zarejestrowana tylko na INSERT/UPDATE. Dla tych tabel czyszczenie
 * usuwa dane, ale nie cofa liczników w `client_activity` — trzeba je przeliczyć osobno.
 */
export const TABLES_WITHOUT_DELETE_TRIGGER: SimulatorTable[] = [
  SIMULATOR_TABLE.nutritionDay,
  SIMULATOR_TABLE.measurement,
];

/**
 * Czy symulator utworzył wiersz, czy tylko zmienił istniejący.
 *
 * Rozróżnienie jest krytyczne dla czyszczenia. Nagranie ćwiczenia zakłada workouts-service,
 * a replikację treningu z planu — automatyzacja; symulator jedynie przestawia im status.
 * Skasowanie takiego wiersza zniszczyłoby dane, których nie stworzył. To samo dotyczy dnia
 * diety i pomiaru, które klientka mogła wpisać wcześniej.
 */
export type SimulatorWriteOp = "insert" | "update";

export type SimulatorWriteEntry = {
  id: string;
  createdAt: string;
  traineeId: string;
  tableName: SimulatorTable;
  rowId: string;
  createdBy: string;
  metadata: {
    eventDate?: string;
    mode?: "single" | "bulk";
    label?: string;
    op?: SimulatorWriteOp;
    /** Kolumny sprzed zmiany — wystarczą do przywrócenia wiersza przy czyszczeniu. */
    before?: Record<string, unknown>;
  } | null;
};

type LogParams = {
  traineeId: string;
  createdBy: string;
  tableName: SimulatorTable;
  rowId: string;
  eventDate: string;
  mode: "single" | "bulk";
  op: SimulatorWriteOp;
  /** Wymagane przy `op: "update"`, żeby dało się cofnąć zmianę zamiast kasować cudzy wiersz. */
  before?: Record<string, unknown>;
  label?: string;
};

/**
 * Odnotowuje zapis. Świadomie NIE przerywa operacji, gdy log się nie zapisze: dane klientki są
 * już w bazie i lepiej mieć je bez wpisu w rejestrze (do posprzątania ręcznie) niż udawać,
 * że zapis się nie udał.
 *
 * `onConflict` na (tabela, wiersz) sprawia, że ponowne odhaczenie tego samego wiersza nadpisze
 * wpis — w tym `before`. Dlatego przy aktualizacji zachowujemy najstarszy zapisany stan: to
 * on jest prawdziwym „przed symulatorem".
 */
export const logSimulatorWrite = async ({
  traineeId,
  createdBy,
  tableName,
  rowId,
  eventDate,
  mode,
  op,
  before,
  label,
}: LogParams): Promise<void> => {
  const { data: existing } = await supabase
    .from("simulator_write_log")
    .select("metadata")
    .match({ tableName, rowId })
    .maybeSingle();

  const existingMetadata = (existing?.metadata ?? null) as SimulatorWriteEntry["metadata"];
  // Pierwszy zapisany `before` wygrywa — kolejne zmiany nadpisałyby go stanem, który sam
  // pochodzi już od symulatora.
  const preservedBefore = existingMetadata?.before ?? before;
  const preservedOp = existingMetadata?.op ?? op;

  await supabase.from("simulator_write_log").upsert(
    {
      traineeId,
      createdBy,
      tableName,
      rowId,
      metadata: {
        eventDate,
        mode,
        op: preservedOp,
        ...(preservedBefore ? { before: preservedBefore } : {}),
        ...(label ? { label } : {}),
      } as Json,
    },
    { onConflict: "tableName,rowId" },
  );
};

/** Usuwa wpis rejestru dla wiersza, którego zmianę właśnie cofnięto ręcznie. */
export const forgetSimulatorWrite = async (tableName: SimulatorTable, rowId: string): Promise<void> => {
  await supabase.from("simulator_write_log").delete().match({ tableName, rowId });
};

/** Wpis rejestru dla konkretnego wiersza — po nim poznajemy, czy symulator go stworzył, czy tylko zmienił. */
export const getSimulatorWrite = async (
  tableName: SimulatorTable,
  rowId: string,
): Promise<SimulatorWriteEntry | null> => {
  const { data, error } = await supabase
    .from("simulator_write_log")
    .select("*")
    .match({ tableName, rowId })
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return (data as SimulatorWriteEntry | null) ?? null;
};

/**
 * Czy cofnięcie pojedynczej akcji może skasować wiersz.
 *
 * Wolno tylko wtedy, gdy symulator sam go stworzył. Wiersz, który tylko nadpisał (odhaczenie
 * klientki, replikacja z planu, zaplanowane nagranie), trzeba przywrócić ze snapshotu — inaczej
 * „cofnij" niszczy dane, których nie stworzyliśmy, i rejestr nie ma już czego odtworzyć.
 */
export const wasCreatedBySimulator = (entry: SimulatorWriteEntry | null): boolean =>
  entry != null && (entry.metadata?.op ?? "insert") === "insert";

export const listSimulatorWrites = async (traineeId: string): Promise<SimulatorWriteEntry[]> => {
  const { data, error } = await supabase
    .from("simulator_write_log")
    .select("*")
    .eq("traineeId", traineeId)
    .order("createdAt", { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []) as SimulatorWriteEntry[];
};

export type CleanupResult = {
  deletedRows: number;
  revertedRows: number;
  /** Wiersze zmienione przez symulator, których nie dało się przywrócić (brak snapshotu). */
  strandedRows: number;
  /** Wiersze, które zniknęły, zanim doszło do przywrócenia — snapshot był, ale nie miał na co wrócić. */
  vanishedRows: number;
  /** Tabele, w których skasowano wiersze, a które nie mają triggera na DELETE. */
  staleCounterTables: SimulatorTable[];
};

/**
 * Cofa wszystko, co symulator zrobił danej klientce.
 *
 * Wiersze utworzone przez symulator są kasowane, a te tylko zmienione — przywracane ze
 * snapshotu `before`. Kolejność jest istotna: najpierw dotykamy tabel docelowych, żeby zdążyły
 * odpalić triggery, a rejestr sprzątamy na końcu.
 */
export const clearSimulatorWrites = async (traineeId: string): Promise<CleanupResult> => {
  const entries = await listSimulatorWrites(traineeId);

  if (entries.length === 0) {
    return { deletedRows: 0, revertedRows: 0, strandedRows: 0, vanishedRows: 0, staleCounterTables: [] };
  }

  const deletedTables = new Set<SimulatorTable>();
  let deletedRows = 0;
  let revertedRows = 0;
  let strandedRows = 0;
  let vanishedRows = 0;

  // Grupujemy kasowanie po tabeli, żeby zejść z liczbą zapytań; przywracanie jest z natury
  // per wiersz, bo każdy ma własny snapshot.
  const toDelete = new Map<SimulatorTable, string[]>();

  for (const entry of entries) {
    const op = entry.metadata?.op ?? "insert";
    const before = entry.metadata?.before;

    if (op === "insert") {
      toDelete.set(entry.tableName, [...(toDelete.get(entry.tableName) ?? []), entry.rowId]);
      continue;
    }

    if (!before) {
      strandedRows += 1;
      continue;
    }

    // `.select()` po to, żeby odróżnić „przywrócono" od „wiersza już nie ma". Bez tego UPDATE
    // trafiający w zero wierszy kończy się sukcesem, a my kasowalibyśmy wpis rejestru w przekonaniu,
    // że dane wróciły — podczas gdy przepadły razem z wierszem skasowanym w międzyczasie.
    const { data: reverted, error } = await supabase
      .from(entry.tableName)
      .update(before as never)
      .eq("id", entry.rowId)
      .select("id");

    if (error) {
      throw new Error(`Nie udało się przywrócić wiersza w ${entry.tableName}: ${error.message}`);
    }

    if (!reverted?.length) {
      vanishedRows += 1;
      continue;
    }

    revertedRows += 1;
  }

  for (const [tableName, rowIds] of toDelete) {
    const { error } = await supabase.from(tableName).delete().in("id", rowIds);

    if (error) {
      throw new Error(`Nie udało się usunąć wpisów z ${tableName}: ${error.message}`);
    }

    deletedRows += rowIds.length;
    deletedTables.add(tableName);
  }

  const { error: logError } = await supabase
    .from("simulator_write_log")
    .delete()
    .in(
      "id",
      entries.map((entry) => entry.id),
    );

  if (logError) {
    throw new Error(logError.message);
  }

  return {
    deletedRows,
    revertedRows,
    strandedRows,
    vanishedRows,
    staleCounterTables: [...deletedTables].filter((table) => TABLES_WITHOUT_DELETE_TRIGGER.includes(table)),
  };
};
